import { ValidationGlobalStore } from './validation-global-store';
import {
  ValidatorRule,
  ValidatorStore,
  Validator,
} from './validation.interfaces';

export abstract class ValidatorStoreBase<TValidator extends Validator>
  implements ValidatorStore
{
  private validatorsMap = new Map<string, TValidator>();

  private unhookStateChangedCallbacks: (() => void)[] = [];

  constructor(public readonly globalStore: ValidationGlobalStore) {}

  getRules(): ValidatorRule[] {
    let validators = [...this.validatorsMap.values()];
    let rules = validators.flatMap(v => v.rules);
    return rules;
  }

  getInvalidRules(): ValidatorRule[] {
    let validators = [...this.validatorsMap.values()];
    let invalidRules = validators.flatMap(v => v.invalidRules);
    return invalidRules;
  }

  getValidRules(): ValidatorRule[] {
    let validators = [...this.validatorsMap.values()];
    let validRules = validators.flatMap(v => v.validRules);
    return validRules;
  }

  getRulesByMetadata(
    rulePredicate: (metadata: any) => boolean,
  ): ValidatorRule[] {
    let validators = [...this.validatorsMap.values()];
    let rules = validators.flatMap(v =>
      v.rules.filter(r => rulePredicate(r.metadata)),
    );
    return rules;
  }

  validateByMetadata(rulePredicate: (metadata: any) => boolean): void {
    let rules = this.getRulesByMetadata(rulePredicate);
    rules.forEach(r => r.validate());
  }

  validate() {
    let validators = [...this.validatorsMap.values()];
    validators.forEach(v => v.validate());
  }

  private _isValid: boolean;
  get isValid(): boolean {
    return this._isValid;
  }

  protected getValidator(key: string): TValidator {
    let hasValidator = this.hasValidator(key);
    if (!hasValidator) return null;
    let validator = this.validatorsMap.get(key);
    return validator;
  }

  protected addValidator(key: string, validator: TValidator) {
    let hasValidator = this.hasValidator(key);
    if (hasValidator) {
      throw `Validator for key '${key}' is already added to the validation store.`;
    }
    this.validatorsMap.set(key, validator);
    let unhookStateChangedCallback = validator.onStateChanged(validator =>
      this.validatorStateChanged(validator),
    );
    this.unhookStateChangedCallbacks.push(unhookStateChangedCallback);
    this.updateValidationState();
    return validator;
  }

  protected removeValidator(key: string) {
    let hasValidator = this.hasValidator(key);
    if (!hasValidator) return;
    this.validatorsMap.delete(key);
    this.updateValidationState();
  }

  protected hasValidator(key: string): boolean {
    return this.validatorsMap.has(key);
  }

  private updateValidationState() {
    let validators = [...this.validatorsMap.values()];
    let isValid = validators.every(v => v.isValid);
    if (this.isValid == isValid) return;

    this._isValid = isValid;
    this.isValidStateChanged();
  }

  private validatorStateChanged(validator: Validator) {
    this.updateValidationState();
  }

  // Can be overridden
  protected isValidStateChanged() {}

  dispose() {
    this.unhookStateChangedCallbacks.forEach(c => c());
  }
}
